import React from 'react'
import { Table } from 'semantic-ui-react'
import styled from 'styled-components'
import { useTabletAndBelowMediaQuery } from 'layout'
import { formatMoney, formatShortDate } from 'utility/crm/dates'
import { Avatar, Card, Muted, Pill, Row, StagePill } from './ui'

const ClickableRow = styled(Table.Row)`
    cursor: pointer;
    &:focus-visible {
        outline: 2px solid #2563eb;
        outline-offset: -2px;
    }
`

const statusLabel = deal => (deal.Status === 'lost' ? 'Lost' : deal.Stage)

const Flags = ({ deal }) => (
    <>
        {deal.Status === 'open' && deal.IsStuck ? <Pill $tone="amber">{deal.DaysInStage}d in stage</Pill> : null}{' '}
        {deal.Status === 'open' && deal.IsPastClose ? <Pill $tone="red">Close date passed</Pill> : null}
    </>
)

/**
 * Deals as a table (cards on phones). Rows open with Enter.
 * @param {{ deals: import('utility/crm/types').CrmDeal[], onOpen: (deal: import('utility/crm/types').CrmDeal) => void }} props
 */
export const DealList = ({ deals, onOpen }) => {
    const isMobile = useTabletAndBelowMediaQuery()
    const onKey = (event, deal) => {
        if (event.key === 'Enter' && event.target === event.currentTarget) onOpen(deal)
    }

    if (isMobile) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {deals.map(deal => (
                    <Card key={deal.DealId} role="button" tabIndex={0} aria-label={`Open deal ${deal.AccountName}: ${deal.Name}`} onClick={() => onOpen(deal)} onKeyDown={e => onKey(e, deal)} style={{ cursor: 'pointer' }}>
                        <Row $between $wrap>
                            <strong>{deal.AccountName}</strong>
                            <StagePill stage={statusLabel(deal)} />
                        </Row>
                        <div>{deal.Name}</div>
                        <Muted>
                            {formatMoney(deal.Amount)} ARR{deal.CloseDate ? ` · Close ${formatShortDate(deal.CloseDate)}` : ''}
                        </Muted>
                        <div style={{ marginTop: 4 }}>
                            <Flags deal={deal} />
                        </div>
                    </Card>
                ))}
            </div>
        )
    }

    return (
        <Table celled selectable compact="very" aria-label="Deals">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Account</Table.HeaderCell>
                    <Table.HeaderCell>Deal</Table.HeaderCell>
                    <Table.HeaderCell>Stage</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Amount</Table.HeaderCell>
                    <Table.HeaderCell>Close</Table.HeaderCell>
                    <Table.HeaderCell>Flags</Table.HeaderCell>
                    <Table.HeaderCell>Owner</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {deals.map(deal => (
                    <ClickableRow key={deal.DealId} tabIndex={0} aria-label={`Open deal ${deal.AccountName}: ${deal.Name}`} onClick={() => onOpen(deal)} onKeyDown={e => onKey(e, deal)}>
                        <Table.Cell>
                            <strong>{deal.AccountName}</strong>
                            {deal.ContactName ? (
                                <div>
                                    <Muted>{deal.ContactName}</Muted>
                                </div>
                            ) : null}
                        </Table.Cell>
                        <Table.Cell>{deal.Name}</Table.Cell>
                        <Table.Cell>
                            <StagePill stage={statusLabel(deal)} />
                        </Table.Cell>
                        <Table.Cell textAlign="right">{formatMoney(deal.Amount)}</Table.Cell>
                        <Table.Cell>{deal.CloseDate ? formatShortDate(deal.CloseDate) : '–'}</Table.Cell>
                        <Table.Cell>
                            <Flags deal={deal} />
                        </Table.Cell>
                        <Table.Cell>{deal.OwnerName ? <Avatar name={deal.OwnerName} size={26} /> : <Muted>–</Muted>}</Table.Cell>
                    </ClickableRow>
                ))}
            </Table.Body>
        </Table>
    )
}
